import React, { useRef } from "react";
import { Listbox } from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/outline';
import { cn } from "./cn";
import FloatingPanel from './FloatingPanel';
import { INPUT_BASE } from './Field';

function MobileSelector({ options, value, onChange, label, disabled }) {
  const anchorRef = useRef(null);
  const selected = options.find(option => option.id === value);
  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      {({ open }) => <>
        <Listbox.Button ref={anchorRef} aria-label={label} className={cn(INPUT_BASE, 'flex items-center justify-between gap-3 text-left')}>
          <span className="min-w-0 truncate">{selected?.label || label}</span>
          <ChevronDownIcon className={cn('h-4 w-4 shrink-0 text-bun-stone', open && 'rotate-180')} />
        </Listbox.Button>
        <FloatingPanel as={Listbox.Options} static autoFocus open={open} anchorRef={anchorRef} width="anchor" align="start" className="m-0 list-none p-1.5 focus:outline-none sm:hidden">
          {options.map(option => (
            <Listbox.Option key={option.id} value={option.id} className={({ active }) => cn(
              'flex min-h-11 cursor-pointer items-center gap-3 rounded-bun px-3 py-2 text-bun-body',
              active && 'bg-bun-mist'
            )}>
              {({ selected: isSelected }) => <>
                <span className={cn('min-w-0 flex-1', isSelected ? 'font-semibold text-bun-blue' : 'font-book text-bun-text')}>{option.label}</span>
                {isSelected && <CheckIcon className="h-4 w-4 shrink-0 text-bun-blue" />}
              </>}
            </Listbox.Option>
          ))}
        </FloatingPanel>
      </>}
    </Listbox>
  );
}

/**
 * Bun segmented control: mist-deep track, white raised thumb on the active
 * segment. For small view switches (Chapters / Tasks / Strategy, Current /
 * After trades). options: [{ id, label }]. mobilePills owns the shared compact
 * navigation-pill sizing below sm; callers only supply layout around the control.
 */
export default function SegmentedControl({ options, value, onChange, size = "md", variant = "default", mobileSelect = false, mobilePills = false, disabled = false, 'aria-label': ariaLabel = 'View', className }) {
  const navigation = variant === 'navigation';
  const pad = size === "sm" ? "px-3 py-[5px]" : "px-[13px] py-[6px]";
  return (
    <>
    {mobileSelect && <div className="w-full sm:hidden"><MobileSelector options={options} value={value} onChange={onChange} label={ariaLabel} disabled={disabled} /></div>}
    <div
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        "bg-bun-mist-deep rounded-bun p-[3px] gap-[2px] font-instrument",
        navigation ? 'grid w-full sm:grid-cols-2 lg:grid-cols-[repeat(var(--segment-count),minmax(0,1fr))]' : 'inline-flex w-fit',
        mobileSelect && (navigation ? 'hidden sm:grid' : 'hidden sm:inline-flex'),
        mobilePills && 'flex w-full flex-wrap gap-1 bg-transparent p-0 sm:gap-[2px] sm:bg-bun-mist-deep sm:p-[3px]',
        mobilePills && (navigation ? 'sm:grid' : 'sm:inline-flex sm:w-fit sm:flex-nowrap'),
        className
      )}
      style={navigation ? { '--segment-count': options.length } : undefined}
    >
      {options.map((opt) => {
        const isActive = opt.id === value;
        return (
          <button
            key={opt.id}
            role="tab"
            id={opt.tabId}
            aria-controls={opt.panelId}
            aria-label={navigation || mobilePills ? opt.label : undefined}
            type="button"
            disabled={disabled}
            aria-selected={isActive}
            onClick={() => onChange(opt.id)}
            className={cn(
              "min-h-11 sm:min-h-0 text-bun-meta rounded-[8px] cursor-pointer whitespace-nowrap disabled:cursor-not-allowed disabled:opacity-50",
              pad,
              navigation && 'min-w-0 whitespace-normal text-left sm:px-3 sm:py-2',
              "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-bun-blue",
              isActive
                ? "bg-bun-surface font-semibold text-bun-text shadow-bun-sm"
                : "bg-transparent font-strong text-bun-stone hover:text-bun-text",
              mobilePills && 'min-h-8 rounded-full border px-3 py-1 sm:rounded-[8px] sm:border-0 sm:py-2',
              mobilePills && (isActive
                ? 'border-bun-ink bg-bun-ink text-bun-on-ink shadow-none sm:bg-bun-surface sm:text-bun-text sm:shadow-bun-sm'
                : 'border-bun-line text-bun-slate sm:text-bun-stone')
            )}
          >
            {mobilePills && <span className="sm:hidden">{opt.mobileLabel || opt.label}</span>}
            <span className={mobilePills ? 'hidden sm:block' : undefined}>
              {navigation ? <><span className="block text-bun-body font-strong">{opt.label}</span>{opt.description && <span className="mt-0.5 block text-bun-label font-book normal-case tracking-normal text-bun-stone">{opt.description}</span>}</> : opt.label}
            </span>
          </button>
        );
      })}
    </div>
    </>
  );
}
